import HttpFactory from "~/repository/http.factory";
import type { PaginatedResponse } from "~/types/shared/response/paginated.response";
import type { PaginationRequest } from "~/types/shared/request/pagination.request";
import type { ScodiListResponse } from "~/types/scodi/response/scodi.list.response";
import type { SelectInput } from "~/types/shared/select.input";
import type { ScodiResponse } from "~/types/scodi/response/scodi.response";
import type { CreateScodiRequest } from "~/types/scodi/request/create.scodi.request";
import type { FormValidationErrorResponse } from "~/types/shared/response/form.validation.error.response";
import type { UpdateScodiRequest } from "~/types/scodi/request/update.scodi.request";
import type { FlashResponse } from "~/types/shared/response/flash.response";
import type { ResultResponse } from "~/types/shared/response/result.response";

export default class ScodiClient extends HttpFactory {
  private ADMIN_RESOURCE = "/admin/scodi";
  private CHOICES_RESOURCE = "/choices/scodi";
  private headers: HeadersInit = {
    "Content-type": "application/json",
  };

  async findAll(
    request?: PaginationRequest
  ): Promise<PaginatedResponse<ScodiListResponse>> {
    return await this.call<PaginatedResponse<ScodiListResponse>>(
      "POST",
      `${this.ADMIN_RESOURCE}/list`,
      this.getAuthenticationHeaders(),
      request
    );
  }

  async getMachineTypes(): Promise<SelectInput[]> {
    return await this.call<SelectInput[]>(
      "GET",
      `${this.CHOICES_RESOURCE}/machine_types`,
      this.getAuthenticationHeaders()
    );
  }

  async getScodiStatuses(): Promise<SelectInput[]> {
    return await this.call<SelectInput[]>(
      "GET",
      `${this.CHOICES_RESOURCE}/statuses`,
      this.getAuthenticationHeaders()
    );
  }

  async get(uuid: string): Promise<ScodiResponse> {
    return await this.call<ScodiResponse>(
      "GET",
      `${this.ADMIN_RESOURCE}/get/${uuid}`,
      this.getAuthenticationHeaders()
    );
  }

  async enableStandby(uuid: string): Promise<ResultResponse> {
    return await this.call<ResultResponse>(
      this.HTTP_POST,
      `${this.ADMIN_RESOURCE}/standby/enable/${uuid}`,
      this.getAuthenticationHeaders()
    );
  }

  async disableStandby(uuid: string): Promise<ResultResponse> {
    return await this.call<ResultResponse>(
      this.HTTP_POST,
      `${this.ADMIN_RESOURCE}/standby/disable/${uuid}`,
      this.getAuthenticationHeaders()
    );
  }

  async create(
    request: CreateScodiRequest
  ): Promise<FlashResponse | FormValidationErrorResponse> {
    return await this.call<FlashResponse | FormValidationErrorResponse>(
      "POST",
      `${this.ADMIN_RESOURCE}/create`,
      this.getAuthenticationHeaders(),
      request
    );
  }

  async update(
    request: UpdateScodiRequest
  ): Promise<FlashResponse | FormValidationErrorResponse> {
    return await this.call<FlashResponse | FormValidationErrorResponse>(
      "POST",
      `${this.ADMIN_RESOURCE}/update`,
      this.getAuthenticationHeaders(),
      request
    );
  }

  async updateLocation(
    request: UpdateScodiLocationRequest
  ): Promise<FlashResponse | FormValidationErrorResponse> {
    return await this.call<FlashResponse | FormValidationErrorResponse>(
      "POST",
      `${this.ADMIN_RESOURCE}/location/update`,
      this.getAuthenticationHeaders(),
      request
    );
  }
}
